<script lang="ts" setup>
import { externalLinks } from '#core/utils/external-links'
import { ConsultationSourceEnum } from '@forgd/contract/types'
import { Button } from '@forgd/ui-kit/button'
import { Card, CardContent } from '@forgd/ui-kit/card'
import { SectionHeader, SectionHeaderDescription, SectionHeaderTitle } from '@forgd/ui-kit/section-header'
import { ArrowUpRightIcon } from '@heroicons/vue/24/outline'

const { destination } = useConsultationDestination()
const { captureConsultationClick } = useConsultationTracking()

function handleCardClick(url: string) {
  captureConsultationClick(ConsultationSourceEnum.ReadyToStart, url)
}

const items = computed(() => [
  {
    title: 'Apply for full-service advisory',
    description: 'Work directly with our experts for personalized insights, deeper analysis, and hands-on advisory for your next milestone.',
    url: destination.value.to,
    ctaText: 'Schedule a Consultation',
    image: '/icons/full-service-advisory.svg',
    target: destination.value.target,
    external: destination.value.external,
  },
  {
    title: 'Explore free tools',
    description: 'Browse our collection of free tools to support your research, analysis, and project operations.',
    url: externalLinks.tools,
    ctaText: 'Learn More',
    image: '/icons/tools.svg',
    target: '_blank' as const,
    external: true,
  },
])
</script>

<template>
  <div data-el="ReadyToStart" class="space-y-10 md:space-y-5">
    <SectionHeader>
      <SectionHeaderTitle>Ready to start with Forgd?</SectionHeaderTitle>
      <SectionHeaderDescription class="md:w-1/2">
        Engage with our free tools, and apply the learnings from our research data.
      </SectionHeaderDescription>
    </SectionHeader>
    <div class="grid grid-cols-1 md:grid-cols-2 gap-5">
      <Card v-for="item in items" :key="item.title" class="bg-dark">
        <CardContent class="flex flex-col h-full gap-5 md:gap-4">
          <img :src="item.image" :alt="item.title" class="size-15">
          <div class="flex flex-col">
            <div class="text-base text-primal-inverted">
              {{ item.title }}
            </div>
            <div class="text-sm text-secondary-inverted">
              {{ item.description }}
            </div>
          </div>
          <div class="mt-auto">
            <Button as-child variant="outline-inverted" size="sm">
              <NuxtLink
                :to="item.url"
                :external="item.external"
                :target="item.target"
                :rel="item.external ? 'noopener noreferrer' : undefined"
                @click="handleCardClick(item.url)"
              >
                {{ item.ctaText }}
                <ArrowUpRightIcon />
              </NuxtLink>
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  </div>
</template>
